import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  DeviceInfoResponse,
  DeviceMetaInfoMetricsResponse,
  DeviceSubCategoryResponse,
  DownloadCSVResponse,
  GetDeviceMakeListResponse,
  LocationHierarchyResponse,
  MeterRelayStatusGraphViewResponse,
  MeterRelayStatusListViewResponse,
  RealTimeCLockListViewResponse,
  RFDeviceDetailsResponse
} from '../../types';
import {
  DeviceMetaInfoMetricsRecord,
  DeviceSubCategoryRecord,
  LocationHierarchyRecord,
  MeterRelayStatusGraphViewRecord
} from '../../types/records/device-management';
import { RFDeviceDetailsModifiedRecord } from '../../types/records/device-information';
import { DownloadCSVRecord } from '../../types/records/download-csv';
import { Option } from '../../types/other';

export const deviceManagementEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'hesApi'
  >
) => ({
  getLocationHierarchy: builder.query<
    LocationHierarchyRecord,
    { searchQuery: string }
  >({
    query: ({ searchQuery }) => ({
      url: `device-management/location-hierarchy-list?${searchQuery}`,
      method: 'GET'
    }),
    transformResponse: (
      response: LocationHierarchyResponse
    ): LocationHierarchyRecord => {
      const records = response.data.records;
      return records[0];
    }
  }),
  getDeviceIdentifier: builder.query<
    LocationHierarchyRecord,
    { searchQuery: string }
  >({
    query: ({ searchQuery }) => ({
      url: `device-management/device-info?${searchQuery}`,
      method: 'GET'
    }),
    transformResponse: (
      response: DeviceInfoResponse
    ): LocationHierarchyRecord => {
      const deviceIdentifiers = response.data.records.map((record) => ({
        label: record.deviceIdentifier,
        value: record.deviceIdentifier
      }));

      return { device_identifier: deviceIdentifiers };
    }
  }),
  getDeviceMetaInfoMetrics: builder.query<DeviceMetaInfoMetricsRecord[], void>({
    query: () => ({
      url: `device-management/device-meta-info-metrics`,
      method: 'GET'
    }),
    transformResponse: (
      response: DeviceMetaInfoMetricsResponse
    ): DeviceMetaInfoMetricsRecord[] => {
      return response.data.records || [];
    }
  }),
  getDeviceSubCategory: builder.query<DeviceSubCategoryRecord[], void>({
    query: () => ({
      url: `device-management/device-sub-category-list`,
      method: 'GET'
    }),
    transformResponse: (
      response: DeviceSubCategoryResponse
    ): DeviceSubCategoryRecord[] => {
      return response.data.records || [];
    }
  }),
  getMeterRelayStatusListView: builder.query<
    MeterRelayStatusListViewResponse,
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `device-management/device-relay-status-list-view${searchParams}`,
      method: 'GET'
    })
  }),
  getMeterRelayStatusGraphView: builder.query<
    MeterRelayStatusGraphViewRecord[],
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `device-management/device-relay-status-pie-chart-view${searchParams}`,
      method: 'GET'
    }),
    transformResponse: (
      response: MeterRelayStatusGraphViewResponse
    ): MeterRelayStatusGraphViewRecord[] => {
      return response.data.records || [];
    }
  }),
  getRealtimeClockListView: builder.query<
    RealTimeCLockListViewResponse,
    { searchParams: string }
  >({
    query: ({ searchParams }) => ({
      url: `device-management/device-clock-status${searchParams}`,
      method: 'GET'
    })
  }),
  getRFDeviceDetails: builder.query<
    RFDeviceDetailsModifiedRecord[],
    { searchQuery: string }
  >({
    query: ({ searchQuery }) => ({
      url: `device-management/fetch-rf-device-details${searchQuery}`,
      method: 'GET'
    }),
    transformResponse: (
      response: RFDeviceDetailsResponse
    ): RFDeviceDetailsModifiedRecord[] => {
      if (!response || !response.data || !response.data.records) return [];
      return (
        response.data.records.map((record) => ({
          label: record.deviceSerial,
          value: record.deviceIp,
          deviceIdentifier: record.deviceIdentifier
        })) || []
      );
    }
  }),
  getDCUSerial: builder.query<Option[], { searchQuery: string }>({
    query: ({ searchQuery }) => ({
      url: `device-management/device-info${searchQuery}`,
      method: 'GET'
    }),
    transformResponse: (response: DeviceInfoResponse): Option[] => {
      const dcuSerials = response.data.records.map((record) => ({
        label: record.deviceSerial,
        value: record.deviceIdentifier
      }));

      return dcuSerials;
    }
  }),
  getDeviceMakeList: builder.query<GetDeviceMakeListResponse, void>({
    query: () => ({
      url: `device-management/device-make`
    })
  }),
  downloadAllDeviceInfoData: builder.query<DownloadCSVRecord, void>({
    query: () => ({
      url: `device-management/download-device-info-report`,
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse,
      meta
    ): DownloadCSVRecord => {
      const contentType = meta?.response?.headers?.get('content-type');
      console.log(response);
      console.log(contentType);

      // Check if the content type is CSV or text, indicating a file download
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('application/octet-stream')
      ) {
        // If the response is a Blob (CSV file), return it as a csvBlob
        return { csvBlob: response as Blob };
      }

      // If it's a JSON response, handle it as DownloadCSVResponse
      if ('data' in response) {
        const downloadResponse = response as DownloadCSVResponse;
        const url = downloadResponse?.data?.records[0]?.url;
        return { url };
      }

      // If neither case matches, throw an error or handle accordingly
      throw new Error('Unexpected response format');
    }
  }),
  downloadRTCData: builder.query<DownloadCSVRecord, string>({
    query: (params) => ({
      url: `device-management/device-clock-status/download-report${params}`,
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse,
      meta
    ): DownloadCSVRecord => {
      const contentType = meta?.response?.headers?.get('content-type');
      console.log(response);
      console.log(contentType);

      // Check if the content type is CSV or text, indicating a file download
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('application/octet-stream')
      ) {
        // If the response is a Blob (CSV file), return it as a csvBlob
        return { csvBlob: response as Blob };
      }

      // If it's a JSON response, handle it as DownloadCSVResponse
      if ('data' in response) {
        const downloadResponse = response as DownloadCSVResponse;
        const url = downloadResponse?.data?.records[0]?.url;
        return { url };
      }

      // If neither case matches, throw an error or handle accordingly
      throw new Error('Unexpected response format');
    }
  }),
  downloadFactoryFileFormat: builder.query<DownloadCSVRecord, void>({
    query: () => ({
      url: `device-management/factory-file-format/download`,
      responseHandler: async (response) => {
        const contentType = response.headers.get('content-type');

        // If the content type is JSON, parse the response as JSON
        if (contentType?.includes('application/json')) {
          return await response.json(); // Parse and return JSON
        }

        // Otherwise, treat it as a Blob (for files like CSV)
        return await response.blob(); // Handle as Blob (binary data)
      }
    }),
    transformResponse: (
      response: Blob | DownloadCSVResponse,
      meta
    ): DownloadCSVRecord => {
      const contentType = meta?.response?.headers?.get('content-type');
      console.log(response);
      console.log(contentType);

      // Check if the content type is CSV or text, indicating a file download
      if (
        contentType?.includes('text/csv') ||
        contentType?.includes('application/zip')
      ) {
        // If the response is a Blob (CSV file), return it as a csvBlob
        return { csvBlob: response as Blob };
      }

      // If it's a JSON response, handle it as DownloadCSVResponse
      if ('data' in response) {
        const downloadResponse = response as DownloadCSVResponse;
        const url = downloadResponse?.data?.records[0]?.url;
        return { url };
      }

      // If neither case matches, throw an error or handle accordingly
      throw new Error('Unexpected response format');
    }
  })
});
